import React, { useEffect, useState } from 'react'

import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'

import { fetchClients } from '../common/helpers'
import axiosClient from '../service/axiosClient'
import ClientDTO from '../types/Client'
import { PostsFilter } from '../types/Post'

interface Props {
    filter: PostsFilter
    setFilter: React.Dispatch<React.SetStateAction<PostsFilter>>
}

export const PostsListFilter = ({filter, setFilter}: Props) => {
    const [clients, setClients] = useState<ClientDTO[]>([])
    const [showApprovalModal, setShowApprovalModal] = useState(false)
    const [approvalEmail, setApprovalEmail] = useState('')
    const [isSending, setIsSending] = useState(false)
    const {t} = useTranslation()

    useEffect(() => {
        fetchClients().then(setClients).catch(console.error)
    }, [])

    const handleDownloadExport = async () => {
        try {
            const response = await axiosClient.get('post-export/download', {
                params: filter.clientId ? { clientId: filter.clientId } : {},
                responseType: 'blob'
            })

            const url = window.URL.createObjectURL(new Blob([response.data]))
            const link = document.createElement('a')
            link.href = url
            const contentDisposition = response.headers['content-disposition']
            const fileName = contentDisposition
                ? contentDisposition.split('filename=')[1]?.replace(/"/g, '')
                : 'prispevky.xlsx'
            link.setAttribute('download', fileName)
            document.body.appendChild(link)
            link.click()
            link.remove()
            window.URL.revokeObjectURL(url)

            toast.success(t('export_downloaded_success'))
        } catch (error) {
            console.error('Error downloading export:', error)
            toast.error(t('export_download_error'))
        }
    }

    const handleSendForApproval = async () => {
        if (!approvalEmail) {
            toast.error(t('please_enter_email'))
            return
        }

        if (!filter.clientId) {
            toast.error(t('please_select_client'))
            return
        }

        setIsSending(true)
        try {
            await axiosClient.post('post-export/send-for-approval', null, {
                params: {
                    email: approvalEmail,
                    clientId: filter.clientId,
                }
            })

            toast.success(t('approval_link_sent'))
            setShowApprovalModal(false)
            setApprovalEmail('')
        } catch (error) {
            console.error('Error sending for approval:', error)
            toast.error(t('approval_send_error'))
        } finally {
            setIsSending(false)
        }
    }

    return (
        <>
            <div className="rounded-content-body mb-4">
                <div className="row d-flex align-items-end">
                    <div className="col-md-4">
                        <div className={'form-group'}>
                            <label htmlFor="clientId">
                                {t('client')}
                            </label>
                            <select
                                id="clientId"
                                name="clientId"
                                className="form-select"
                                onChange={(e) => {
                                    setFilter((old) => ({...old, clientId: e.target.value}))
                                }}>
                                <option value="">{t('select')}</option>
                                {clients.map((option) => (
                                    <option key={option.id}
                                            value={option.id}>{option.companyName || (`${option.firstName} ${option.lastName}`)}</option>))}
                            </select>
                        </div>
                    </div>
                    <div className="col-md-3">
                        <div className="form-check mb-2">
                            <input
                                id="showHiddenPosts"
                                type="checkbox"
                                className="form-check-input"
                                checked={filter.hidden}
                                onChange={(e) => {
                                    setFilter((old) => ({...old, hidden: e.target.checked}))
                                }}/>
                            <label className="form-check-label" htmlFor="showHiddenPosts">
                                {t('show_hidden_posts')}
                            </label>
                        </div>
                    </div>
                    <div className="col-md-5">
                        <div className="d-flex gap-2 justify-content-end">
                            <button
                                className="amani-primary"
                                onClick={handleDownloadExport}>
                                {t('download_export')}
                            </button>
                            <button
                                className="amani-primary"
                                onClick={() => {
                                    if (!filter.clientId) {
                                        toast.error(t('please_select_client_first'))
                                        return
                                    }
                                    setShowApprovalModal(true)
                                }}>
                                {t('send_for_approval')}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Approval Modal */}
            {showApprovalModal && (
                <div className="modal show d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">{t('send_for_approval')}</h5>
                                <button
                                    type="button"
                                    className="btn-close"
                                    onClick={() => setShowApprovalModal(false)}
                                ></button>
                            </div>
                            <div className="modal-body">
                                <div className="form-group">
                                    <label htmlFor="approvalEmail">{t('recipient_email')}</label>
                                    <input
                                        type="email"
                                        id="approvalEmail"
                                        className="form-control"
                                        value={approvalEmail}
                                        onChange={(e) => setApprovalEmail(e.target.value)}
                                        placeholder={t('enter_email')}
                                    />
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={() => setShowApprovalModal(false)}
                                >
                                    {t('cancel')}
                                </button>
                                <button
                                    type="button"
                                    className="amani-primary"
                                    onClick={handleSendForApproval}
                                    disabled={isSending}
                                >
                                    {isSending ? t('sending') : t('submit_send')}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    )
}
